import { Component, inject, ChangeDetectorRef, OnInit, OnDestroy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { Subscription, switchMap, map, interval } from "rxjs";
import { environment } from "../../../environments/environment";
import { SocketService } from "../../core/event/services/socket.service";
import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { RhynothonHttpService } from "./services/rhynothon-http.service";
import { EventMetadata, EventSource } from "../../core/models/event-metadata.model";

@Component({
  standalone: true,
  selector: "app-rhynothon",
  templateUrl: "./rhynothon.component.html",
  styleUrls: ["./rhynothon.component.scss"],
  imports: [CommonModule],
  providers: [SocketService, HttpClient, RhynothonHttpService],
})
export class RhynothonComponent implements OnInit, OnDestroy {
  public hashRhynothon!: string;
  public streamerId: string = "";
  public paramSubscriptions: Subscription[] = [];
  public timerSubscription?: Subscription;

  // Configurações do Rhynothon
  public days: number = 0;
  public hours: number = 0;
  public minutes: number = 0;
  public seconds: number = 0;
  public isActive: boolean = false;

  // Configurações visuais
  public textColor: string = "#ffffff";
  public backgroundColor: string = "rgba(0, 0, 0, 0.8)";
  public transparentBackground: boolean = false;

  // Cronômetro interno
  public currentTime: number = 0; // Time em segundos
  public displayDays: number = 0;
  public displayHours: number = 0;
  public displayMinutes: number = 0;
  public displaySeconds: number = 0;

  // 🚀 SISTEMA HÍBRIDO com performance.now() (imune a mudanças de horário)
  public totalDurationSeconds: number = 0;
  public hybridStartedAt: Date | null = null;
  
  // 🎯 Ponto de sincronização com servidor (substitui timeOffset)
  private syncPoint = {
    serverTime: 0,        // Timestamp do servidor no último evento
    perfTimestamp: 0      // performance.now() no momento do sync
  };

  // 🎯 Sistema de notificações de eventos (dual-icon system)
  public showNotification: boolean = false;
  public notificationData: {
    platformIconPath?: string;
    platformColor?: string;
    typeIcon?: string; // Material Icon name
    typeIconPath?: string; // PNG image path
    contributorName: string;
    displayValue: string;
  } | null = null;

  // Configuração de PLATAFORMAS
  private readonly PLATFORM_CONFIG = {
    TWITCH: {
      iconPath: 'assets/images/platforms/twitch.svg',
      color: '#9146FF',
    },
    KICK: {
      iconPath: 'assets/images/platforms/kick.svg',
      color: '#53FC18',
    },
    YOUTUBE: {
      iconPath: 'assets/images/platforms/youtube.png',
      color: '#FF0000',
    },
    SITE: {
      iconPath: 'assets/images/logo_index.png',
      color: '#9c27b0',
    },
  };

  // Configuração de TIPOS DE EVENTO
  private readonly EVENT_TYPE_CONFIG = {
    SUB: {
      icon: 'star',
      label: 'Sub',
    },
    GIFT_SUB: {
      icon: 'card_giftcard',
      label: 'Gift',
    },
    BITS: {
      iconPath: 'assets/images/platforms/bits.png',
      label: 'Bits',
    },
    KICKS: {
      iconPath: 'assets/images/platforms/kicks.png',
      label: 'Kicks',
    },
    SUPERCHAT: {
      icon: 'chat_bubble',
      label: 'Super Chat',
    },
    MEMBER: {
      icon: 'workspace_premium',
      label: 'Membro',
    },
    DONATION: {
      icon: 'favorite',
      label: 'Doação',
    },
    SALE: {
      icon: 'shopping_cart',
      label: 'Venda',
    },
  };

  // MAPEAMENTO de EventSource → Platform + Type
  private readonly EVENT_SOURCE_MAP = {
    [EventSource.TWITCH_SUB]: {
      platform: 'TWITCH' as const,
      type: 'SUB' as const,
    },
    [EventSource.KICK_SUB]: {
      platform: 'KICK' as const,
      type: 'SUB' as const,
    },
    [EventSource.YOUTUBE_MEMBER]: {
      platform: 'YOUTUBE' as const,
      type: 'MEMBER' as const,
    },
    [EventSource.TWITCH_BITS]: {
      platform: 'TWITCH' as const,
      type: 'BITS' as const,
    },
    [EventSource.KICK_KICKS]: {
      platform: 'KICK' as const,
      type: 'KICKS' as const,
    },
    [EventSource.YOUTUBE_SUPERCHAT]: {
      platform: 'YOUTUBE' as const,
      type: 'SUPERCHAT' as const,
    },
    [EventSource.DONATION]: {
      platform: null,
      type: 'DONATION' as const,
    },
    [EventSource.SALE]: {
      platform: null,
      type: 'SALE' as const,
    },
  };

  // Flags
  public isLocal = false;

  // Services
  public titleService = inject(Title);
  public activatedRoute = inject(ActivatedRoute);
  public socketService = inject(SocketService);
  public http = inject(HttpClient);
  public rhynothonHttpService = inject(RhynothonHttpService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.isLocal = environment.stage === "local";
    this.initializeParams();
    this.connectToSocket();
    this.startTimer();
  }

  ngOnDestroy(): void {
    this.socketService.disconnect();
    this.paramSubscriptions.forEach((sub) => sub.unsubscribe());
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  private initializeParams(): void {
    const paramSub = this.activatedRoute.params
      .pipe(
        switchMap((params: any) => {
          this.hashRhynothon = params?.id;
          this.titleService.setTitle("Rhynothon Timer - The Cool Rhyno");
          return this.activatedRoute.queryParams;
        }),
        map((queryParams: any) => {
          return queryParams;
        })
      )
      .subscribe();

    this.paramSubscriptions.push(paramSub);
  }

  private connectToSocket(): void {
    if (this.hashRhynothon) {
      this.socketService.connect();

      this.rhynothonHttpService.getRhynothonByHash(this.hashRhynothon).subscribe({
        next: (response: any) => {
          if (response) {
            this.loadRhynothonData(response);
          }
        },
        error: (error) => {
          // Silent error handling
        }
      });

      this.socketService
        .onEvent(`event:${this.hashRhynothon}`)
        .subscribe({
          next: (eventPayload: any) => {
            this.loadRhynothonData(eventPayload);
          },
          error: (error) => {
            // Silent error handling
          }
        });
    }
  }

  private loadRhynothonData(data: any): void {
    if (data) {
      const dadosAnteriores = {
        days: this.days,
        hours: this.hours, 
        minutes: this.minutes,
        seconds: this.seconds,
        isActive: this.isActive
      };

      // 🚀 Atualizar ponto de sincronização (HTTP inicial e eventos WebSocket)
      if (data.serverTime) {
        this.syncPoint = {
          serverTime: data.serverTime,
          perfTimestamp: performance.now()
        };
        console.log(`[Rhynothon OBS] Sync atualizado: serverTime=${new Date(data.serverTime).toISOString()}, perfNow=${this.syncPoint.perfTimestamp.toFixed(0)}ms`);
      }

      // 🎯 NOVO: Processar evento (doação, venda, sub, etc)
      if (data.event) {
        this.showEventNotification(data.event);
      }

      // 🚀 SISTEMA HÍBRIDO PURO - Frontend calcula o tempo total
      this.totalDurationSeconds = (data.configuredDurationSeconds || 0) + (data.earningsDurationSeconds || 0);
      this.hybridStartedAt = data.startedAt ? new Date(data.startedAt) : null;
      this.isActive = data.isActive || false;
      
      // Calcular tempo restante em tempo real
      this.currentTime = this.calculateHybridTime();
      
      // Configurações visuais
      this.textColor = data.textColor || "#ffffff";
      this.backgroundColor = data.backgroundColor || "rgba(0, 0, 0, 0.8)";
      this.transparentBackground = data.transparentBackground || false;

      this.updateDisplayTime();

      this.cdr.detectChanges();
    }
  }

  private calculateTotalSeconds(): number {
    return (this.days * 24 * 60 * 60) + 
           (this.hours * 60 * 60) + 
           (this.minutes * 60) + 
           this.seconds;
  }

  private startTimer(): void {
    // 🚀 Timer híbrido puro a cada 100ms para fluidez
    this.timerSubscription = interval(100).subscribe(() => {
      if (this.isActive && this.hybridStartedAt && this.totalDurationSeconds > 0) {
        // Calcular tempo real baseado em timestamp
        this.currentTime = this.calculateHybridTime();
        this.updateDisplayTime();
        this.cdr.detectChanges();

        // Verificar se chegou a zero
        if (this.currentTime <= 0) {
          this.isActive = false;
          this.hybridStartedAt = null;
        }
      }
    });
  }

  private updateDisplayTime(): void {
    const totalSeconds = Math.floor(this.currentTime); // Garantir número inteiro
    
    this.displayDays = Math.floor(totalSeconds / (24 * 60 * 60));
    this.displayHours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    this.displayMinutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    this.displaySeconds = Math.floor(totalSeconds % 60);
  }

  public formatTime(value: number): string {
    return value.toString().padStart(2, '0');
  }

  public get containerStyles() {
    // Estilos aplicados no timer-display (box do cronômetro)
    const styles: any = {
      color: this.textColor,           // Cor do texto configurável
      backgroundColor: this.backgroundColor, // Cor do box configurável
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      fontSize: '48px',
      textAlign: 'center'
    };

    return styles;
  }

  /**
   * 🚀 SISTEMA HÍBRIDO: Calcular tempo restante usando ponto de sincronização + performance.now()
   * (Imune a mudanças de horário do sistema)
   */
  private calculateHybridTime(): number {
    if (!this.hybridStartedAt || !this.totalDurationSeconds) {
      return 0;
    }

    // 🎯 Calcular tempo do servidor usando ponto de sincronização + performance.now()
    const msSinceSync = performance.now() - this.syncPoint.perfTimestamp;
    const correctedNow = this.syncPoint.serverTime + msSinceSync;
    const elapsed = (correctedNow - this.hybridStartedAt.getTime()) / 1000;
    return Math.max(0, this.totalDurationSeconds - elapsed);
  }

  /**
   * 🎯 Exibir notificação de evento (dual-icon system)
   */
  private showEventNotification(event: EventMetadata): void {
    console.log('[RHYNOTHON] 🎯 Evento recebido:', event);
    
    const mapping = this.EVENT_SOURCE_MAP[event.source as keyof typeof this.EVENT_SOURCE_MAP];
    
    if (!mapping) {
      console.warn('[RHYNOTHON] ❌ Tipo de evento desconhecido:', event.source);
      return;
    }
    
    console.log('[RHYNOTHON] 📍 Mapping encontrado:', mapping);

    // Determinar tipo de evento
    let eventType: keyof typeof this.EVENT_TYPE_CONFIG = mapping.type;
    
    // Se for gift subscription
    if (event.isGift) {
      eventType = 'GIFT_SUB';
    }

    // Buscar configurações
    // Se não tiver plataforma (DONATION/SALE), usa config do SITE
    const platformConfig = mapping.platform 
      ? this.PLATFORM_CONFIG[mapping.platform as keyof typeof this.PLATFORM_CONFIG]
      : this.PLATFORM_CONFIG.SITE;
    const typeConfig = this.EVENT_TYPE_CONFIG[eventType];

    console.log('[RHYNOTHON] 🎨 Platform config:', platformConfig);
    console.log('[RHYNOTHON] 🎨 Type config:', typeConfig);
    
    // Validações extras para debug
    if (mapping.platform && !platformConfig) {
      console.error('[RHYNOTHON] ❌ PLATFORM_CONFIG não encontrado para:', mapping.platform);
      return;
    }

    if (!typeConfig) {
      console.warn('[RHYNOTHON] ❌ Configuração de tipo não encontrada:', eventType);
      return;
    }

    // Construir notificação (donations/sales não têm platformIcon, apenas typeIcon)
    // Suporta tanto 'icon' (Material Icons) quanto 'iconPath' (imagens PNG)
    const typeIcon = 'icon' in typeConfig ? typeConfig.icon : undefined;
    const typeIconPath = 'iconPath' in typeConfig ? typeConfig.iconPath : undefined;

    this.notificationData = {
      platformIconPath: platformConfig?.iconPath,
      platformColor: platformConfig?.color || '#666',
      typeIcon: typeIcon,
      typeIconPath: typeIconPath,
      contributorName: event.contributorName,
      displayValue: this.formatTimeAdded(event.timeAddedSeconds || 0),
    };

    console.log('[RHYNOTHON] 📦 Notification data:', this.notificationData);

    this.showNotification = true;

    setTimeout(() => {
      this.showNotification = false;
      this.cdr.detectChanges();
    }, 5000);
    
  }

  /**
   * 🎯 Formatar tempo adicionado para exibição (+1min, +30s, etc)
   */
  private formatTimeAdded(seconds: number): string {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return secs > 0 ? `+${mins}min ${secs}s` : `+${mins}min`;
    }
    return `+${seconds}s`;
  }

  // 🔧 MÉTODOS DE TESTE (somente em desenvolvimento)
  public onTestNotification(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.TWITCH_SUB,
        contributorName: 'TestUser123',
        timeAddedSeconds: 120, // 2 minutos
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }

  public onTestDonation(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.DONATION,
        contributorName: 'Doador Generoso',
        timeAddedSeconds: 300, // 5 minutos
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }

  public onTestSale(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.SALE,
        contributorName: 'Cliente VIP',
        timeAddedSeconds: 600, // 10 minutos
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }

  public onTestYoutubeMember(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.YOUTUBE_MEMBER,
        contributorName: 'YouTuber Fan',
        timeAddedSeconds: 180, // 3 minutos
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }

  public onTestKickSub(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.KICK_SUB,
        contributorName: 'Kick Supporter',
        timeAddedSeconds: 90, // 1.5 minutos
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }
}